/**
 * LabourRatesView.js
 *
 * Read/write view of lt_labour_rates lookup table within Cost Breakdown context.
 * Shows Day Shift and Night Shift rates side by side.
 * Reuses existing backend routes at /api/cost-breakdown/labour-rates.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import AuditLogModal from '../../common/AuditLogModal';

const formatCurrency = (val) => {
  if (!val && val !== 0) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);
};

const LabourRatesView = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('cbLabourRates', 'global', true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [toast, setToast] = useState(null);
  const [showAudit, setShowAudit] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const result = await authenticatedApiCall(instance, accounts, 'GET', '/cost-breakdown/labour-rates');
      if (result.data?.success) setItems(result.data.data || []);
    } catch (err) {
      console.error('Error fetching labour rates:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleAdd = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/labour-rates', {
        ds_trade: 'New Trade'
      });
      fetchItems();
      showToast('Row added');
    } catch (err) {
      showToast('Failed to add', 'error');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      account_number: item.account_number || '',
      ds_trade: item.ds_trade || '',
      ds_work_schedule: item.ds_work_schedule || '',
      ds_straight: item.ds_straight || '',
      ds_overtime: item.ds_overtime || '',
      ds_double: item.ds_double || '',
      ds_total_wages: item.ds_total_wages || '',
      ds_addition: item.ds_addition || '',
      ds_notes: item.ds_notes || '',
      ns_trade: item.ns_trade || '',
      ns_work_schedule: item.ns_work_schedule || '',
      ns_straight: item.ns_straight || '',
      ns_overtime: item.ns_overtime || '',
      ns_double: item.ns_double || '',
      ns_total_wages: item.ns_total_wages || '',
      ns_addition: item.ns_addition || '',
      ns_notes: item.ns_notes || '',
    });
  };

  const handleSave = async () => {
    try {
      const payload = { ...editForm };
      ['ds_straight', 'ds_overtime', 'ds_double', 'ds_total_wages', 'ds_addition',
        'ns_straight', 'ns_overtime', 'ns_double', 'ns_total_wages', 'ns_addition'].forEach(k => {
        payload[k] = parseFloat(payload[k]) || null;
      });
      await authenticatedApiCall(instance, accounts, 'PUT', `/cost-breakdown/labour-rates/${editingId}`, payload);
      setEditingId(null);
      fetchItems();
      showToast('Saved');
    } catch (err) {
      showToast('Failed to save', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this labour rate record?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/cost-breakdown/labour-rates/${id}`);
      fetchItems();
      showToast('Deleted');
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  const filtered = search
    ? items.filter(i =>
      (i.ds_trade || '').toLowerCase().includes(search.toLowerCase()) ||
      (i.account_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (i.ns_trade || '').toLowerCase().includes(search.toLowerCase()))
    : items;

  const cellStyle = { padding: '6px 8px', fontSize: '0.8rem', borderBottom: '1px solid #e5e7eb' };
  const inputStyle = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem', width: '100%', boxSizing: 'border-box' };
  const numInputStyle = { ...inputStyle, width: '70px', textAlign: 'right' };

  return (
    <div style={{ padding: '24px' }}>
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, padding: '12px 20px', borderRadius: '8px', color: 'white', fontWeight: '500', backgroundColor: toast.type === 'error' ? '#dc3545' : '#198754', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#1f2937' }}>Labour Rates</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
            Global lookup table (lt_labour_rates) — Day Shift and Night Shift rates by trade
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          <button onClick={() => setShowAudit(true)} style={{ padding: '8px 16px', backgroundColor: '#0f766e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
            📜 Audit Log
          </button>
          <button onClick={handleAdd} style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
            + Add Trade
          </button>
        </div>
      </div>
      <AuditLogModal
        isOpen={showAudit}
        onClose={() => setShowAudit(false)}
        endpoint="/cost-breakdown/audit-log?table_name=lt_labour_rates"
        title="Labour Rates Audit Log"
        accentColor="#0f766e"
      />

      <div style={{ marginBottom: '12px' }}>
        <input type="text" placeholder="Search by trade or account..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #dee2e6', fontSize: '0.9rem', width: '300px', outline: 'none' }}
        />
        <span style={{ marginLeft: '12px', color: '#6b7280', fontSize: '0.85rem' }}>{filtered.length} records</span>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
          <thead>
            <tr>
              <th style={{ ...cellStyle, fontWeight: '600', fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb' }} rowSpan={2}>Acct #</th>
              <th colSpan={7} style={{ ...cellStyle, fontWeight: '600', fontSize: '0.75rem', color: '#1d4ed8', textAlign: 'center', backgroundColor: '#e7f1ff' }}>Day Shift</th>
              <th colSpan={7} style={{ ...cellStyle, fontWeight: '600', fontSize: '0.75rem', color: '#7c3aed', textAlign: 'center', backgroundColor: '#f5f3ff' }}>Night Shift</th>
              <th style={{ ...cellStyle, backgroundColor: '#f9fafb' }} rowSpan={2}></th>
            </tr>
            <tr>
              {['Trade', 'Schedule', 'Straight', 'OT', 'Double', 'Total', 'Notes'].map(h => (
                <th key={`ds-${h}`} style={{ ...cellStyle, fontWeight: '600', fontSize: '0.65rem', color: '#1d4ed8', textTransform: 'uppercase', backgroundColor: '#f0f5ff' }}>{h}</th>
              ))}
              {['Trade', 'Schedule', 'Straight', 'OT', 'Double', 'Total', 'Notes'].map(h => (
                <th key={`ns-${h}`} style={{ ...cellStyle, fontWeight: '600', fontSize: '0.65rem', color: '#7c3aed', textTransform: 'uppercase', backgroundColor: '#faf5ff' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={16} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={16} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No records found.</td></tr>
            ) : filtered.map(item => {
              const isEditing = editingId === item.id;
              if (isEditing) {
                return (
                  <tr key={item.id} style={{ backgroundColor: '#fefce8' }}>
                    <td style={cellStyle}><input style={{ ...inputStyle, width: '60px' }} value={editForm.account_number} onChange={(e) => setEditForm({ ...editForm, account_number: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#f0f5ff' }}><input style={inputStyle} value={editForm.ds_trade} onChange={(e) => setEditForm({ ...editForm, ds_trade: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#f0f5ff' }}><input style={{ ...inputStyle, width: '80px' }} value={editForm.ds_work_schedule} onChange={(e) => setEditForm({ ...editForm, ds_work_schedule: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#f0f5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ds_straight} onChange={(e) => setEditForm({ ...editForm, ds_straight: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#f0f5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ds_overtime} onChange={(e) => setEditForm({ ...editForm, ds_overtime: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#f0f5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ds_double} onChange={(e) => setEditForm({ ...editForm, ds_double: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#f0f5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ds_total_wages} onChange={(e) => setEditForm({ ...editForm, ds_total_wages: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#f0f5ff' }}><input style={inputStyle} value={editForm.ds_notes} onChange={(e) => setEditForm({ ...editForm, ds_notes: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#faf5ff' }}><input style={inputStyle} value={editForm.ns_trade} onChange={(e) => setEditForm({ ...editForm, ns_trade: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#faf5ff' }}><input style={{ ...inputStyle, width: '80px' }} value={editForm.ns_work_schedule} onChange={(e) => setEditForm({ ...editForm, ns_work_schedule: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#faf5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ns_straight} onChange={(e) => setEditForm({ ...editForm, ns_straight: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#faf5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ns_overtime} onChange={(e) => setEditForm({ ...editForm, ns_overtime: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#faf5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ns_double} onChange={(e) => setEditForm({ ...editForm, ns_double: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#faf5ff' }}><input style={numInputStyle} type="number" step="0.01" value={editForm.ns_total_wages} onChange={(e) => setEditForm({ ...editForm, ns_total_wages: e.target.value })} /></td>
                    <td style={{ ...cellStyle, backgroundColor: '#faf5ff' }}><input style={inputStyle} value={editForm.ns_notes} onChange={(e) => setEditForm({ ...editForm, ns_notes: e.target.value })} /></td>
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', gap: '3px' }}>
                        <button onClick={handleSave} style={{ padding: '3px 8px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ padding: '3px 8px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafbfc'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <td style={{ ...cellStyle, fontWeight: '500', color: '#1f2937' }}>{item.account_number || '—'}</td>
                  <td style={{ ...cellStyle, fontWeight: '500', backgroundColor: '#fafbff' }}>{item.ds_trade || '—'}</td>
                  <td style={{ ...cellStyle, backgroundColor: '#fafbff', color: '#6b7280' }}>{item.ds_work_schedule || '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fafbff' }}>{item.ds_straight ? formatCurrency(item.ds_straight) : '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fafbff' }}>{item.ds_overtime ? formatCurrency(item.ds_overtime) : '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fafbff' }}>{item.ds_double ? formatCurrency(item.ds_double) : '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fafbff', fontWeight: '600', color: '#1d4ed8' }}>{item.ds_total_wages ? formatCurrency(item.ds_total_wages) : '—'}</td>
                  <td style={{ ...cellStyle, backgroundColor: '#fafbff', color: '#6b7280', fontSize: '0.75rem' }}>{item.ds_notes || ''}</td>
                  <td style={{ ...cellStyle, fontWeight: '500', backgroundColor: '#fbfaff' }}>{item.ns_trade || '—'}</td>
                  <td style={{ ...cellStyle, backgroundColor: '#fbfaff', color: '#6b7280' }}>{item.ns_work_schedule || '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fbfaff' }}>{item.ns_straight ? formatCurrency(item.ns_straight) : '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fbfaff' }}>{item.ns_overtime ? formatCurrency(item.ns_overtime) : '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fbfaff' }}>{item.ns_double ? formatCurrency(item.ns_double) : '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', backgroundColor: '#fbfaff', fontWeight: '600', color: '#7c3aed' }}>{item.ns_total_wages ? formatCurrency(item.ns_total_wages) : '—'}</td>
                  <td style={{ ...cellStyle, backgroundColor: '#fbfaff', color: '#6b7280', fontSize: '0.75rem' }}>{item.ns_notes || ''}</td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', gap: '3px' }}>
                      <button onClick={() => startEdit(item)} style={{ padding: '2px 6px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>Edit</button>
                      <button onClick={() => handleDelete(item.id)} style={{ padding: '2px 6px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#dc3545' }}>Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LabourRatesView;
